import React, { useEffect, useState } from 'react';
import {
  Button,
  Form,
  Label,
  Modal,
  Pagination,
  Popup,
  Select,
  Table,
} from 'semantic-ui-react';
import { API, showError, showSuccess, timestamp2string } from '../helpers';
import { ITEMS_PER_PAGE } from '../constants';

const USER_TYPE_OPTIONS = [
  { key: 'all', text: '全部主体', value: '' },
  { key: '2', text: '企业', value: '2' },
  { key: '1', text: '个人', value: '1' },
];

const SILENT_OPTIONS = [
  { key: 'all', text: '静默授权（全部）', value: '' },
  { key: '2', text: '已授权', value: '2' },
  { key: '1', text: '待授权', value: '1' },
  { key: '0', text: '未申请', value: '0' },
  { key: '-1', text: '已拒绝', value: '-1' },
];

const dateOnly = (timestamp) => {
  if (!timestamp || timestamp <= 0) {
    return '-';
  }
  return timestamp2string(timestamp).split(' ')[0];
};

const silentLabel = (status, valid) => {
  if (status === 2) {
    return valid
      ? { text: '已授权', color: 'green' }
      : { text: '已过期', color: 'orange' };
  }
  if (status === 1) return { text: '待授权', color: 'blue' };
  if (status === -1) return { text: '已拒绝', color: 'red' };
  return { text: '未申请', color: 'grey' };
};

const authLabel = (status) => {
  if (status === 2) return { text: '已认证', color: 'green' };
  if (status === 1 || (status >= 3 && status <= 6)) {
    return { text: '认证中', color: 'blue' };
  }
  return { text: '未认证', color: 'grey' };
};

const ContractAccountsTable = () => {
  const [accounts, setAccounts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState(1);
  const [keyword, setKeyword] = useState('');
  const [userType, setUserType] = useState('');
  const [silentStatus, setSilentStatus] = useState('');
  const [detail, setDetail] = useState({ open: false, data: null, loading: false });
  const [confirmInfo, setConfirmInfo] = useState({
    open: false,
    title: '',
    content: '',
    danger: false,
    onConfirm: null,
  });

  const loadAccounts = async (page = 1, kw = keyword, type = userType, silent = silentStatus) => {
    setLoading(true);
    let url = `/api/contract/account/?p=${page - 1}`;
    if (kw) {
      url += `&keyword=${encodeURIComponent(kw)}`;
    }
    if (type !== '') {
      url += `&user_type=${type}`;
    }
    if (silent !== '') {
      url += `&silent_auth_status=${silent}`;
    }
    const res = await API.get(url);
    const { success, message, data, total: count } = res.data;
    if (success) {
      setAccounts(data || []);
      setTotal(count || 0);
      setActivePage(page);
    } else {
      showError(message);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadAccounts(1).catch((reason) => showError(reason));
  }, []);

  const openDetail = async (account) => {
    setDetail({ open: true, data: null, loading: true });
    const res = await API.get(`/api/contract/account/${account.id}`);
    const { success, message, data } = res.data;
    if (success) {
      setDetail({ open: true, data, loading: false });
    } else {
      showError(message);
      setDetail({ open: false, data: null, loading: false });
    }
  };

  const syncAccount = async (account) => {
    const res = await API.post(`/api/contract/account/${account.id}/sync`);
    const { success, message } = res.data;
    if (success) {
      showSuccess(message || '同步完成');
      await loadAccounts(activePage);
    } else {
      showError(message);
    }
  };

  const askConfirm = (title, content, onConfirm, danger = true) => {
    setConfirmInfo({ open: true, title, content, danger, onConfirm });
  };

  const cancelAccount = (account) => {
    askConfirm(
      '注销电子签账号',
      `确定注销 ${account.name || account.uid}（${account.union_id}）吗？上游账号信息将被清除且无法找回，本地映射同步删除。`,
      async () => {
        const res = await API.post(`/api/contract/account/${account.id}/cancel`);
        const { success, message } = res.data;
        if (success) {
          showSuccess(message || '已注销');
          await loadAccounts(activePage);
        } else {
          showError(message);
        }
      }
    );
  };

  const closeConfirm = () => {
    setConfirmInfo((prev) => ({ ...prev, open: false, onConfirm: null }));
  };

  const runConfirm = async () => {
    const handler = confirmInfo.onConfirm;
    setConfirmInfo((prev) => ({ ...prev, open: false, onConfirm: null }));
    if (handler) {
      await handler();
    }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    showSuccess('已复制');
  };

  return (
    <>
      <Form onSubmit={() => loadAccounts(1)}>
        <Form.Group>
          <Form.Input
            icon='search'
            iconPosition='left'
            placeholder='搜索 uid / unionId / esign_uid / 名称 / 手机号'
            value={keyword}
            onChange={(e, { value }) => setKeyword(value)}
            width={6}
          />
          <Form.Field width={3}>
            <Select
              options={USER_TYPE_OPTIONS}
              value={userType}
              onChange={(e, { value }) => setUserType(value)}
            />
          </Form.Field>
          <Form.Field width={4}>
            <Select
              options={SILENT_OPTIONS}
              value={silentStatus}
              onChange={(e, { value }) => setSilentStatus(value)}
            />
          </Form.Field>
          <Form.Button loading={loading}>查询</Form.Button>
        </Form.Group>
      </Form>
      <Table basic={'very'} compact size='small'>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>ID</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>调用方 uid</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>主体</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>名称 / 手机号</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>unionId</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>实名</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>静默授权</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>回调地址</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>更新时间</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>操作</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {accounts.map((account) => {
            const silent = silentLabel(account.silent_auth_status, !account.silent_auth_expired);
            const auth = authLabel(account.auth_status);
            return (
              <Table.Row key={account.id}>
                <Table.Cell>{account.id}</Table.Cell>
                <Table.Cell>
                  <code style={{ fontSize: '12px' }}>{account.uid}</code>
                  <div style={{ color: '#8a919f', fontSize: '12px' }}>
                    {account.esign_uid}
                  </div>
                </Table.Cell>
                <Table.Cell>{account.user_type === 1 ? '个人' : '企业'}</Table.Cell>
                <Table.Cell>
                  <div style={{ fontWeight: 500 }}>{account.name || '-'}</div>
                  <div style={{ color: '#8a919f', fontSize: '12px' }}>
                    {account.mobile || '-'}
                  </div>
                </Table.Cell>
                <Table.Cell>
                  {account.union_id ? (
                    <code
                      style={{ fontSize: '12px', cursor: 'pointer' }}
                      onClick={() => copy(account.union_id)}
                      title='点击复制'
                    >
                      {account.union_id.slice(0, 12)}...
                    </code>
                  ) : (
                    '-'
                  )}
                </Table.Cell>
                <Table.Cell>
                  <Popup
                    trigger={
                      <Label basic color={auth.color}>
                        {auth.text}
                      </Label>
                    }
                    content={account.auth_status_text || '-'}
                  />
                </Table.Cell>
                <Table.Cell>
                  <Label basic color={silent.color}>
                    {silent.text}
                  </Label>
                  {account.silent_auth_expire > 0 && (
                    <div style={{ color: '#8a919f', fontSize: '12px' }}>
                      {dateOnly(account.silent_auth_expire)}
                    </div>
                  )}
                </Table.Cell>
                <Table.Cell>
                  <code style={{ fontSize: '12px' }}>{account.callback_url || '-'}</code>
                </Table.Cell>
                <Table.Cell>
                  {account.updated_time > 0 ? timestamp2string(account.updated_time) : '-'}
                </Table.Cell>
                <Table.Cell>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    <Button size='tiny' onClick={() => openDetail(account)}>
                      详情
                    </Button>
                    <Button size='tiny' basic onClick={() => syncAccount(account)}>
                      同步
                    </Button>
                    <Button size='tiny' negative onClick={() => cancelAccount(account)}>
                      注销
                    </Button>
                  </div>
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
        <Table.Footer>
          <Table.Row>
            <Table.HeaderCell colSpan='10'>
              <Pagination
                floated='right'
                activePage={activePage}
                onPageChange={(e, { activePage: page }) =>
                  loadAccounts(page).catch((reason) => showError(reason))
                }
                size='small'
                siblingRange={1}
                totalPages={Math.max(1, Math.ceil(total / ITEMS_PER_PAGE))}
              />
              <span style={{ color: '#8a919f' }}>
                共 {total} 个托管账号；uid 由网关按调用方账号加前缀隔离后使用
              </span>
            </Table.HeaderCell>
          </Table.Row>
        </Table.Footer>
      </Table>

      <Modal
        open={detail.open}
        onClose={() => setDetail({ open: false, data: null, loading: false })}
        size='small'
      >
        <Modal.Header>合同账号详情</Modal.Header>
        <Modal.Content>
          {detail.loading && <p>加载中...</p>}
          {detail.data && (
            <Table definition compact size='small'>
              <Table.Body>
                <Table.Row>
                  <Table.Cell>调用方 uid</Table.Cell>
                  <Table.Cell>{detail.data.uid}</Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell>放心签 uid</Table.Cell>
                  <Table.Cell>{detail.data.esign_uid}</Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell>unionId</Table.Cell>
                  <Table.Cell>
                    <code style={{ fontSize: '12px' }}>{detail.data.union_id || '-'}</code>
                  </Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell>主体类型</Table.Cell>
                  <Table.Cell>{detail.data.user_type === 1 ? '个人' : '企业'}</Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell>实名状态</Table.Cell>
                  <Table.Cell>
                    {detail.data.auth_status_text}（{detail.data.auth_status}）
                  </Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell>静默授权</Table.Cell>
                  <Table.Cell>
                    {detail.data.silent_auth_status === 2
                      ? `已授权（到期 ${detail.data.silent_auth_expire > 0 ? dateOnly(detail.data.silent_auth_expire) : '未记录'}）`
                      : detail.data.silent_auth_status === 1
                        ? '待授权'
                        : detail.data.silent_auth_status === -1
                          ? '已拒绝'
                          : '未申请'}
                  </Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell>回调地址</Table.Cell>
                  <Table.Cell>
                    <code style={{ fontSize: '12px' }}>{detail.data.callback_url || '-'}</code>
                  </Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell>实名信息</Table.Cell>
                  <Table.Cell>
                    <pre style={{ maxWidth: 520, whiteSpace: 'pre-wrap', margin: 0 }}>
                      {detail.data.real_name_info || '-'}
                    </pre>
                  </Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell>令牌 / 账号</Table.Cell>
                  <Table.Cell>
                    token_id={detail.data.token_id} / user_id={detail.data.user_id}
                  </Table.Cell>
                </Table.Row>
              </Table.Body>
            </Table>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setDetail({ open: false, data: null, loading: false })}>
            关闭
          </Button>
        </Modal.Actions>
      </Modal>

      <Modal
        open={confirmInfo.open}
        onClose={closeConfirm}
        size='tiny'
        style={{ maxWidth: '480px' }}
      >
        <Modal.Header>{confirmInfo.title}</Modal.Header>
        <Modal.Content>
          <p>{confirmInfo.content}</p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={closeConfirm}>取消</Button>
          <Button color={confirmInfo.danger ? 'red' : 'blue'} onClick={runConfirm}>
            确定
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

export default ContractAccountsTable;
